import React, { useEffect, useState } from "react";
import api from "../api/api";
import toast from "react-hot-toast";
import { Sparkles, CheckCircle2, XCircle, Play, Save, Loader2, Copy } from "lucide-react";

const MODELS = [
  { id: "claude-sonnet-4-5", label: "Claude Sonnet 4.5 (recommended)" },
  { id: "claude-opus-4-8", label: "Claude Opus 4.8 (higher quality, slower)" },
  { id: "claude-haiku-4-5-20251001", label: "Claude Haiku 4.5 (fastest)" },
];

const ERROR_HELP: Record<string, { title: string; hint: string }> = {
  invalid_key: { title: "Invalid API key", hint: "Check your key at console.anthropic.com → API Keys" },
  rate_limit:  { title: "Rate limit exceeded", hint: "Wait a minute and try again" },
  network:     { title: "Network error", hint: "Check your internet connection" },
  invalid_model: { title: "Invalid model", hint: "Pick a different model from the list" },
  unknown:     { title: "Unknown error", hint: "Check the message below" },
};

interface ConfigData {
  isEnabled: boolean;
  claudeApiKey: string | null;
  claudeModel: string;
  autoAnalyzeOnSync: boolean;
  hasApiKey?: boolean;
  lastTestAt: string | null;
  lastTestOk: boolean | null;
  lastTestError: string | null;
}

export default function AiConfig() {
  const [config, setConfig] = useState<ConfigData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [form, setForm] = useState({
    isEnabled: false,
    claudeApiKey: "",
    claudeModel: "claude-sonnet-4-5",
    autoAnalyzeOnSync: false,
    keyDirty: false,
  });

  const load = () => {
    setLoading(true);
    api.get("/ai/config").then(r => {
      const c: ConfigData = r.data;
      setConfig(c);
      setForm({
        isEnabled: !!c.isEnabled,
        claudeApiKey: c.hasApiKey ? "••••••••••••••••" : "",
        claudeModel: c.claudeModel || "claude-sonnet-4-5",
        autoAnalyzeOnSync: !!c.autoAnalyzeOnSync,
        keyDirty: false,
      });
    }).catch(() => setConfig(null)).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const runTest = async () => {
    setTesting(true); setResult(null);
    try {
      const body: any = {};
      if (form.keyDirty && form.claudeApiKey) body.claudeApiKey = form.claudeApiKey;
      const r = await api.post("/ai/test", body);
      setResult(r.data);
      if (r.data.ok) toast.success("Connected");
      else toast.error(ERROR_HELP[r.data.errorType || "unknown"]?.title || "Failed");
    } catch { toast.error("Test failed"); } finally { setTesting(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const body: any = {
        isEnabled: form.isEnabled,
        claudeModel: form.claudeModel,
        autoAnalyzeOnSync: form.autoAnalyzeOnSync,
      };
      if (form.keyDirty && form.claudeApiKey) body.claudeApiKey = form.claudeApiKey;
      await api.put("/ai/config", body);
      toast.success("Saved");
      await runTest();
      load();
    } catch (err: any) { toast.error(err?.response?.data?.message || "Failed to save"); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="p-6 text-gray-500">Loading...</div>;

  const status = !config?.isEnabled ? "⚪ Disabled"
    : !config.claudeApiKey && !form.keyDirty ? "🔵 Not configured"
    : config.lastTestOk ? "🟢 Connected"
    : config.lastTestOk === false ? "🔴 Not connected"
    : "🔵 Ready";

  return (
    <div className="max-w-3xl mx-auto p-6">
      <div className="flex items-center gap-3 mb-6">
        <Sparkles className="text-purple-500" size={28}/>
        <div>
          <h1 className="text-xl font-semibold text-gray-900">AI Integration</h1>
          <p className="text-sm text-gray-500">Connect Claude AI for sentiment analysis and advanced insights</p>
        </div>
      </div>

      <div className="bg-purple-50 border border-purple-200 rounded-2xl p-4 mb-5 text-sm text-purple-800">
        <b>Status:</b> {status}
        {config?.lastTestError && <div className="text-red-600 text-xs mt-1">Last error: {config.lastTestError}</div>}
      </div>

      <div className="bg-white rounded-2xl border border-gray-200 p-5 mb-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-semibold text-gray-800">Claude API</h2>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={form.isEnabled}
              onChange={e => setForm(f => ({...f, isEnabled: e.target.checked}))}
              className="rounded"/>
            Enabled
          </label>
        </div>
        <div className="space-y-3">
          <div>
            <label className="block text-xs text-gray-600 mb-1">
              API Key {config?.hasApiKey && !form.keyDirty && <span className="text-gray-400">(saved — enter to change)</span>}
            </label>
            <input type="password" value={form.claudeApiKey}
              onChange={e => setForm(f => ({...f, claudeApiKey: e.target.value, keyDirty: true}))}
              placeholder="sk-ant-api03-..."
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 outline-none"/>
            <p className="text-[11px] text-gray-500 mt-1">Get one from <a href="https://console.anthropic.com/settings/keys" target="_blank" rel="noreferrer" className="text-purple-600 hover:underline">console.anthropic.com</a></p>
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Model</label>
            <select value={form.claudeModel} onChange={e => setForm(f => ({...f, claudeModel: e.target.value}))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white">
              {MODELS.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
            </select>
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={form.autoAnalyzeOnSync}
              onChange={e => setForm(f => ({...f, autoAnalyzeOnSync: e.target.checked}))}
              className="rounded"/>
            Auto-analyze sentiment when new tickets sync
          </label>
          <div className="flex gap-2 pt-2">
            <button onClick={runTest} disabled={testing}
              className="flex items-center gap-2 border border-gray-300 text-gray-700 px-4 py-2 rounded-lg text-sm hover:bg-gray-50">
              {testing ? <Loader2 size={14} className="animate-spin"/> : <Play size={14}/>}
              Test
            </button>
            <button onClick={save} disabled={saving}
              className="flex items-center gap-2 bg-purple-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-purple-700 ml-auto">
              {saving ? <Loader2 size={14} className="animate-spin"/> : <Save size={14}/>}
              Save
            </button>
          </div>
        </div>

        {result && (
          <div className={`mt-4 rounded-lg p-3 border text-sm ${result.ok ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200"}`}>
            <div className="flex items-start gap-2">
              {result.ok
                ? <CheckCircle2 size={18} className="text-green-600 flex-shrink-0"/>
                : <XCircle size={18} className="text-red-600 flex-shrink-0"/>}
              <div className="flex-1">
                {result.ok ? (
                  <>
                    <div className="text-green-800 font-semibold">Connected</div>
                    <div className="text-xs text-gray-600 mt-1">Model: {result.model} · {result.responseTimeMs}ms</div>
                  </>
                ) : (
                  <>
                    <div className="text-red-800 font-semibold">{ERROR_HELP[result.errorType || "unknown"]?.title || "Failed"}</div>
                    <div className="text-xs text-red-700 mt-1">{result.errorMessage}</div>
                    <div className="text-xs text-gray-600 mt-1">💡 {ERROR_HELP[result.errorType || "unknown"]?.hint}</div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
